import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { forwardStyle, type ForwardStyleProps, styled } from '@glitz/react';
import { useFreezeScroll } from './freeze-scroll';

type PropType = React.HTMLAttributes<HTMLDivElement> & {
  isEnabled: boolean;
  onClose: () => void;
  keepChildrenMounted?: boolean;
  zIndex: number;
  disableFreezeScroll?: boolean;
};

export default React.memo(
  styled(
    forwardStyle(function Overlay({
      children,
      isEnabled,
      onClose,
      keepChildrenMounted = false,
      zIndex,
      disableFreezeScroll = false,
      compose,
      ...restProps
    }: PropType & ForwardStyleProps) {
      const [keepMounted, setKeepMounted] = React.useState(isEnabled);
      const elementRef = React.useRef<HTMLDivElement>(null);
      const onCloseRef = React.useRef(onClose);
      onCloseRef.current = onClose;
      useFreezeScroll(isEnabled && !disableFreezeScroll);

      React.useLayoutEffect(() => {
        const listener = (e: KeyboardEvent) => {
          if (e.key === 'Escape') {
            onCloseRef.current();
          }
        };

        if (isEnabled) {
          window.addEventListener('keydown', listener);
          return () => {
            window.removeEventListener('keydown', listener);
          };
        }
      }, [isEnabled]);

      React.useLayoutEffect(() => {
        if (isEnabled && elementRef.current) {
          setKeepMounted(true);
          const animation = elementRef.current.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: 200,
            easing: 'ease-out',
          });

          return () => {
            animation.reverse();
            animation.onfinish = () => {
              // flushSync forces react to set this state synchronously. This avoids flickering in react 18 new rendering engine.
              ReactDOM.flushSync(() => {
                setKeepMounted(false);
              });
            };
          };
        }
      }, [isEnabled]);

      const backdropClick = React.useRef<boolean>(undefined);
      const handleMouseDown = React.useCallback((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        // We don't want to close the dialog when clicking the dialog content.
        // Make sure the event starts and ends on the same DOM element.
        backdropClick.current = event.target === event.currentTarget;
      }, []);

      const handleBackdropClick = React.useCallback(() => {
        // Ignore the events not coming from the "backdrop".
        if (!backdropClick.current) {
          return;
        }

        backdropClick.current = undefined;
        onCloseRef.current();
      }, []);

      return isEnabled || keepMounted ? (
        <div onClick={handleBackdropClick}>
          <Base css={compose({ zIndex })} {...restProps} ref={elementRef} onMouseDown={handleMouseDown}>
            {children}
          </Base>
        </div>
      ) : keepChildrenMounted ? (
        <>{children}</>
      ) : null;
    }),
  ),
);

// Issue with static Glitz, receives ZIndex.XXX - 1 as z-index
/** @glitz-dynamic */
const Base = styled.div({
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  willChange: 'opacity',
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
});
